import { Button, Center, Group, Modal, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { createContext, useContext, useState, ReactNode } from 'react';
import classes from './FeatureLanding.module.css';

interface FeatureLandingContextType {
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const FeatureLandingContext = createContext<FeatureLandingContextType | undefined>(undefined);

const useFeatureLandingContext = () => {
  const context = useContext(FeatureLandingContext);

  if (!context) {
    throw new Error('Feature Landing components must be used within a FeatureLanding component');
  }

  return context;
};

interface FeatureLandingProps {
  children: ReactNode;
  className?: string;
}

export function FeatureLanding({ children, className }: FeatureLandingProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <FeatureLandingContext.Provider value={{ isModalOpen, openModal, closeModal }}>
      <div className={`${classes.featureLanding} ${className || ''}`}>
        <Center className={classes.container}>
          <Stack className={classes.content}>{children}</Stack>
        </Center>
      </div>
    </FeatureLandingContext.Provider>
  );
}

interface HeaderProps {
  subtitle?: string;
  title: string;
  subtitleAfter?: string;
}

function Header({ subtitle, title, subtitleAfter }: HeaderProps) {
  return (
    <>
      {subtitle && <Text className={classes.headingSubtle}>{subtitle}</Text>}
      <Title className={classes.headingMain} order={1}>
        {title}
      </Title>
      {subtitleAfter && (
        <Title className={classes.headingSub} order={2}>
          {subtitleAfter}
        </Title>
      )}
    </>
  );
}

interface DescriptionProps {
  children: ReactNode;
}

function Description({ children }: DescriptionProps) {
  return <Text className={classes.description}>{children}</Text>;
}

interface ItemsProps {
  children: ReactNode;
}

function Items({ children }: ItemsProps) {
  return <Group className={classes.items}>{children}</Group>;
}

interface ItemProps {
  icon: React.FC<{ style?: React.CSSProperties }>;
  description: string | ReactNode;
}

function Item({ icon: Icon, description }: ItemProps) {
  return (
    <Stack className={classes.item}>
      <Icon style={{ width: '106px', height: '106px' }} />
      {typeof description === 'string' ? <Text className={classes.description}>{description}</Text> : description}
    </Stack>
  );
}

interface ActionsProps {
  children: ReactNode;
}

function Actions({ children }: ActionsProps) {
  return (
    <SimpleGrid cols={2} spacing={20} className={classes.buttons}>
      {children}
    </SimpleGrid>
  );
}

interface ModalButtonProps {
  children: ReactNode;
  variant?: string;
  color?: string;
}

function ModalButton({ children, variant = 'outline', color = 'blue' }: ModalButtonProps) {
  const { openModal } = useFeatureLandingContext();

  return (
    <Button
      data-testid="button-feature-landing-open-modal"
      className={classes.button}
      variant={variant}
      color={color}
      onClick={openModal}
    >
      {children}
    </Button>
  );
}

interface ActionButtonProps {
  children: ReactNode;
  href: string;
  variant?: string;
  color?: string;
  target?: string;
}

function ActionButton({ children, href, variant = 'filled', color = 'blue', target = '_blank' }: ActionButtonProps) {
  return (
    <Button
      data-testid="button-feature-landing-action"
      className={classes.button}
      variant={variant}
      color={color}
      component="a"
      href={href}
      target={target}
    >
      {children}
    </Button>
  );
}

interface ModalComponentProps {
  children: ReactNode;
  title: string;
  size?: number;
}

function ModalComponent({ children, title, size = 815 }: ModalComponentProps) {
  const { isModalOpen, closeModal } = useFeatureLandingContext();

  return (
    <Modal opened={isModalOpen} size={size} onClose={closeModal} title={title}>
      {children}
    </Modal>
  );
}

FeatureLanding.Header = Header;
FeatureLanding.Description = Description;
FeatureLanding.Items = Items;
FeatureLanding.Item = Item;
FeatureLanding.Actions = Actions;
FeatureLanding.ModalButton = ModalButton;
FeatureLanding.ActionButton = ActionButton;
FeatureLanding.Modal = ModalComponent;
